import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';


// Kept outside the component to prevent re-initialization on re-renders
let isTrackingInitialized = false;

const CookieConsentAndAnalytics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);


  // --- Core Tracking Initialization ---
  const initializeTracking = () => {
    if (isTrackingInitialized) return;

    console.log("Initializing GA and GTM...");

    // 1. Initialize Google Analytics
    ReactGA.initialize("G-GHMZ1C8YG8");

    // 2. Initialize Google Tag Manager
    if (!document.getElementById('gtm-script')) {
        
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
      const script = document.createElement('script');
      script.id = 'gtm-script';
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-MP4Z77KK';
      document.head.appendChild(script);
    }

    isTrackingInitialized = true;

    // Send the initial pageview immediately upon granting consent / loading the site
    ReactGA.send({
      hitType: "pageview", 
      page: window.location.pathname + window.location.search
    });
  };

 
  // --- On Mount Check ---
  useEffect(() => {
    const storedConsent = localStorage.getItem('app_cookie_consent');
    
    if (storedConsent) {
      try {
        const { analytics, expiry } = JSON.parse(storedConsent);
        const now = new Date().getTime();

        if (now < expiry) {
          setIsVisible(false);
          // If they previously accepted, fire up tracking
          if (analytics) {
            initializeTracking();
          }
          return; 
        } else {
          localStorage.removeItem('app_cookie_consent');
        }
      } catch (error) {
        console.error("Error parsing consent data", error);
      }
    }
    
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Consent Handlers ---
  const saveConsent = (analyticsAccepted) => {
    const expiryDate = new Date().getTime() + 90 * 24 * 60 * 60 * 1000;
    
    const consentData = {
      analytics: analyticsAccepted,
      expiry: expiryDate,
    };

    localStorage.setItem('app_cookie_consent', JSON.stringify(consentData));
    setIsVisible(false);

    if (analyticsAccepted) {
      initializeTracking();
    }
  };

  const handleAcceptAll = () => saveConsent(true);
  const handleSavePreferences = () => saveConsent(analyticsConsent);
  const handleDeclineOptional = () => saveConsent(false);

  // --- Render ---
  if (!isVisible) return null;

  return (
    <div className="cookie-banner-container">
      <div className="cookie-banner-content">
        <h4>Cookie Preferences</h4>
        <p className='text-sm'>
          We use cookies and local storage to ensure our site functions properly and to analyze our traffic. 
          Please read our <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms">Terms of Use</a> for more information.
        </p>

        {!showSettings ? (
          <div className="cookie-button-group">
            <button onClick={() => setShowSettings(true)} className="btn-customize">Customize</button>
            <button onClick={handleDeclineOptional} className="btn-decline">Decline Optional</button>
            <button onClick={handleAcceptAll} className="btn-accept-all">Accept All</button>
          </div>
        ) : (
          <div className="cookie-settings-container">
            <div className="cookie-option">
              <label>
                <input type="checkbox" checked disabled />
                <strong> Strictly Necessary </strong>
              </label>
              <p className="cookie-description">
                Required for the site to function (e.g., age verification, saving this consent preference). Cannot be disabled.
              </p>
            </div>
            
            <div className="cookie-option">
              <label>
                <input 
                  type="checkbox" 
                  checked={analyticsConsent} 
                  onChange={(e) => setAnalyticsConsent(e.target.checked)} 
                />
                <strong> Google Analytics </strong>
              </label>
              <p className="cookie-description">
                Allows us to analyze site usage and improve the user experience.
              </p>
            </div>

            <div className="cookie-button-group">
              <button onClick={handleSavePreferences} className="btn-save-preferences">Save Preferences</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentAndAnalytics;